/**
 * Close-Approval dialog — admin reviews + edits the timings on an incident
 * pending close approval (Phase 17). Same modal is reused as the "Edit Timings"
 * dialog on already-closed incidents (admin only) via the PATCH /timings route.
 *
 * Props
 *   open, onOpenChange      — controlled visibility
 *   incident                — full incident doc (with assignments)
 *   contractors, services   — lookup arrays from the parent
 *   onSaved(next)           — called with the new server doc after success
 *   mode                    — 'approve' (default) | 'edit-timings'
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Edit, Loader2, AlertTriangle, Clock } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';

import api, { formatApiError } from '../../lib/api';

// ----- helpers --------------------------------------------------------------
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // datetime-local needs YYYY-MM-DDTHH:mm with no timezone suffix
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const minutesBetween = (start, end) => {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return Math.max(0, Math.floor(ms / 60000));
};

const fmtDur = (mins) => {
  const h = Math.floor(mins / 60); const m = mins % 60;
  return `${h}h ${m}m`;
};

// ----- component ------------------------------------------------------------
export const CloseApprovalDialog = ({
  open, onOpenChange, incident, contractors = [], services = [], onSaved, mode = 'approve',
}) => {
  const isEdit = mode === 'edit-timings';

  const [opened, setOpened] = useState('');
  const [closed, setClosed] = useState('');
  const [outcome, setOutcome] = useState('');
  const [assignmentRows, setAssignmentRows] = useState([]); // [{id, started_at, ended_at}]
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // ---- prime state every time the modal opens on a fresh incident ----
  useEffect(() => {
    if (!open || !incident) return;
    setOpened(isoToLocalInput(incident.opened_at));
    setClosed(isoToLocalInput(incident.closed_at || incident.close_requested_at));
    setOutcome(incident.outcome || '');
    setAssignmentRows((incident.assignments || []).map((a) => ({
      id: a.id,
      contractor_id: a.contractor_id,
      service_id: a.service_id,
      started_at: isoToLocalInput(a.started_at),
      ended_at: isoToLocalInput(a.ended_at),
    })));
    setReason('');
  }, [open, incident]);

  const contractorName = (cid) => contractors.find((c) => c.id === cid)?.company_name || '—';
  const serviceName = (sid) => services.find((s) => s.id === sid)?.name || '—';

  // ---- billable-minutes preview — drives the "X mins → Y mins" diff ----
  const totals = useMemo(() => {
    const originalMins = (incident?.assignments || []).reduce(
      (a, x) => a + minutesBetween(x.started_at, x.ended_at), 0);
    const newMins = assignmentRows.reduce(
      (a, x) => a + minutesBetween(localInputToIso(x.started_at), localInputToIso(x.ended_at)), 0);
    return { originalMins, newMins, deltaMins: newMins - originalMins };
  }, [assignmentRows, incident?.assignments]);

  // ---- build the PATCH/approve payload from the form state ----
  const buildPayload = () => ({
    opened_at: localInputToIso(opened),
    closed_at: localInputToIso(closed),
    outcome: outcome.trim() || undefined,
    assignments: assignmentRows.map((r) => ({
      assignment_id: r.id,
      started_at: localInputToIso(r.started_at),
      ended_at: localInputToIso(r.ended_at),
    })),
    reason: reason.trim() || undefined,
  });

  const handleApprove = async () => {
    if (totals.deltaMins !== 0 && !reason.trim()) {
      toast.error('A reason is required when overriding timings');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/approve-close`, buildPayload());
      toast.success(`Incident ${r.data.incident_no} closed`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    const why = window.prompt('Reason for rejection (will return to In Progress):');
    if (!why || !why.trim()) return;
    setRejecting(true);
    try {
      const r = await api.post(`/incidents/${incident.id}/reject-close`, { reason: why.trim() });
      toast.success('Sent back to operator');
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRejecting(false); }
  };

  const handleSaveTimings = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required when editing timings');
      return;
    }
    if (totals.deltaMins === 0 && opened === isoToLocalInput(incident.opened_at)
        && closed === isoToLocalInput(incident.closed_at)) {
      toast.error('No timing changes were made.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      payload.reason = reason.trim();
      const r = await api.patch(`/incidents/${incident.id}/timings`, payload);
      toast.success('Timings updated, bill recalculated');
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!incident) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="close-approval-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#0D5B69] flex items-center gap-2">
            {isEdit
              ? <><Edit className="w-5 h-5" /> Edit Timings — {incident.incident_no}</>
              : <><CheckCircle2 className="w-5 h-5 text-green-600" /> Close Approval — {incident.incident_no}</>}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Adjust the incident open/close window and contractor on-scene times. Bill recomputes immediately. A reason is required for the audit trail.'
              : 'Confirm timings before closing. Any overrides recompute the bill. The audit log will record who approved and what changed.'}
          </DialogDescription>
        </DialogHeader>

        {/* ---- Incident-level timings ------------------------------------- */}
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Incident timings
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Opened at</Label>
              <Input
                type="datetime-local"
                value={opened}
                onChange={(e) => setOpened(e.target.value)}
                className="h-9 mt-1"
                data-testid="approval-opened-at"
              />
            </div>
            <div>
              <Label className="text-xs">Closed at</Label>
              <Input
                type="datetime-local"
                value={closed}
                onChange={(e) => setClosed(e.target.value)}
                className="h-9 mt-1"
                data-testid="approval-closed-at"
              />
            </div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Outcome</Label>
              <Textarea
                rows={2}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                placeholder="e.g. Vehicle recovered, no further action required"
                className="mt-1 text-sm"
                data-testid="approval-outcome"
              />
            </div>
          )}
        </div>

        {/* ---- Per-contractor timings ------------------------------------- */}
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2 mt-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Contractor on-scene times
          </div>
          {assignmentRows.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No contractor assignments on this incident.</p>
          ) : (
            <div className="space-y-2">
              {assignmentRows.map((row, idx) => {
                const sIso = localInputToIso(row.started_at);
                const eIso = localInputToIso(row.ended_at);
                const newMins = minutesBetween(sIso, eIso);
                const reversed = sIso && eIso && new Date(eIso) < new Date(sIso);
                return (
                  <div key={row.id} className="rounded border border-slate-100 bg-slate-50 p-2.5"
                       data-testid={`approval-assignment-row-${idx}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div className="text-xs">
                        <span className="font-semibold text-slate-800">{contractorName(row.contractor_id)}</span>
                        <span className="text-slate-500"> · {serviceName(row.service_id)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {reversed && (
                          <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] gap-1">
                            <AlertTriangle className="w-3 h-3" /> End is BEFORE start — check the dates
                          </Badge>
                        )}
                        {newMins > 1440 && (
                          <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] gap-1">
                            <AlertTriangle className="w-3 h-3" /> Over 24h — check the end DATE
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${(newMins > 1440 || reversed) ? 'border-red-300 text-red-700' : ''}`}>
                          {reversed ? '0h 0m (billed as zero!)' : fmtDur(newMins)}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Dispatched / started</Label>
                        <Input
                          type="datetime-local"
                          value={row.started_at}
                          onChange={(e) => setAssignmentRows((rs) => rs.map(
                            (r, i) => i === idx ? { ...r, started_at: e.target.value } : r))}
                          className="h-8 mt-0.5 text-xs"
                          data-testid={`approval-assignment-started-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Off-scene / ended</Label>
                        <Input
                          type="datetime-local"
                          value={row.ended_at}
                          onChange={(e) => setAssignmentRows((rs) => rs.map(
                            (r, i) => i === idx ? { ...r, ended_at: e.target.value } : r))}
                          className="h-8 mt-0.5 text-xs"
                          data-testid={`approval-assignment-ended-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ---- Diff preview ------------------------------------------------ */}
        <div className={`mt-3 rounded-md px-3 py-2 text-xs flex items-center justify-between ${
          totals.deltaMins === 0
            ? 'bg-slate-50 border border-slate-200 text-slate-600'
            : 'bg-amber-50 border border-amber-200 text-amber-800'
        }`} data-testid="approval-diff-banner">
          <span className="flex items-center gap-2">
            {totals.deltaMins !== 0 && <AlertTriangle className="w-3.5 h-3.5" />}
            <span>
              Billable minutes: {fmtDur(totals.originalMins)} → <b>{fmtDur(totals.newMins)}</b>
              {totals.deltaMins !== 0 && (
                <span className="ml-2 font-mono">
                  ({totals.deltaMins > 0 ? '+' : ''}{totals.deltaMins} min)
                </span>
              )}
            </span>
          </span>
          {totals.deltaMins !== 0 && (
            <span className="text-[10px] uppercase tracking-wide font-bold">Bill will recalculate</span>
          )}
        </div>

        {/* ---- Override reason --------------------------------------------- */}
        {(totals.deltaMins !== 0 || isEdit) && (
          <div className="mt-3">
            <Label className="text-xs">
              Reason for override {isEdit ? '*' : (totals.deltaMins !== 0 ? '*' : '(optional)')}
            </Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Contractor confirmed on-scene at 14:32 via radio log"
              className="mt-1 text-sm"
              data-testid="approval-reason"
            />
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {!isEdit && (
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={rejecting || saving}
              className="gap-1 text-red-700 border-red-200 hover:bg-red-50"
              data-testid="approval-reject-btn"
            >
              {rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Reject
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={isEdit ? handleSaveTimings : handleApprove}
            disabled={saving || rejecting}
            className="bg-green-600 hover:bg-green-700 text-white gap-1"
            data-testid={isEdit ? 'approval-save-timings-btn' : 'approval-confirm-btn'}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isEdit ? 'Save & Recalculate' : 'Approve & Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
